/**
 * @description Die zentrale Hilfeseite, Übersicht mit Quick-Links und FAQ-Bereichen.
 */

import { useEffect } from "react";
import { SearchFocusTarget, User } from "@/App";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Button } from "./ui/button";
import {
  HelpCircle,
  Search,
  FolderTree,
  UserPlus,
  Mail,
  Book,
} from "lucide-react";

interface HelpProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
}

export function Help({
  navigate,
  currentUser,
  onLogout,
  onNavigateToSearchPage,
}: HelpProps) {
  const { t } = useApp();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const secondaryTextStyle = {
    color: "var(--foreground)",
  } as const;
  const answerTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 80%, var(--background))",
  } as const;
  const quickLinkStyles = [
    {
      backgroundColor:
        "color-mix(in srgb, var(--primary) 16%, var(--background))",
      iconColor:
        "color-mix(in srgb, var(--primary) 85%, var(--foreground))",
    },
    {
      backgroundColor:
        "color-mix(in srgb, var(--accent) 16%, var(--background))",
      iconColor:
        "color-mix(in srgb, var(--accent) 85%, var(--foreground))",
    },
    {
      backgroundColor:
        "color-mix(in srgb, color-mix(in srgb, var(--primary) 60%, var(--accent)) 16%, var(--background))",
      iconColor:
        "color-mix(in srgb, color-mix(in srgb, var(--primary) 60%, var(--accent)) 85%, var(--foreground))",
    },
  ] as const;
  const infoCardStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 12%, var(--background))",
    borderColor:
      "color-mix(in srgb, var(--primary) 35%, var(--background))",
  } as const;
  const contactEmail = t("legalNotice.email");

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "var(--background)" }}
    >
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="mb-8">
            <h2 className="text-primary-heading mb-2">{t("help.title")}</h2>
            <p className="text-secondary" style={secondaryTextStyle}>
              {t("help.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="cursor-pointer hover:shadow-md transition-shadow stat-card" onClick={() => scrollToSection("faq-search")}>
              <CardHeader className="space-y-1 pb-6">
                <div
                  className="p-3 rounded-lg w-fit mb-2"
                  style={{ backgroundColor: quickLinkStyles[0].backgroundColor }}
                >
                  <Search className="w-6 h-6" style={{ color: quickLinkStyles[0].iconColor }} />
                </div>
                <CardTitle className="text-primary-heading font-semibold">
                  {t("help.quickLinkSearch")}
                </CardTitle>
                <CardDescription className="card-description-text" style={primaryTextStyle}>
                  {t("help.quickLinkSearchDesc")}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow stat-card" onClick={() => scrollToSection("faq-account")}>
              <CardHeader className="space-y-1 pb-6">
                <div
                  className="p-3 rounded-lg w-fit mb-2"
                  style={{ backgroundColor: quickLinkStyles[0].backgroundColor }}
                >
                  <UserPlus className="w-6 h-6" style={{ color: quickLinkStyles[0].iconColor }} />
                </div>
                <CardTitle className="text-primary-heading font-semibold">
                  {t("help.quickLinkRegistration")}
                </CardTitle>
                <CardDescription className="card-description-text" style={primaryTextStyle}>
                  {t("help.quickLinkRegistrationDesc")}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow stat-card" onClick={() => scrollToSection("faq-adding")}>
              <CardHeader className="space-y-1 pb-6">
                <div
                  className="p-3 rounded-lg w-fit mb-2"
                  style={{ backgroundColor: quickLinkStyles[0].backgroundColor }}
                >
                  <FolderTree className="w-6 h-6" style={{ color: quickLinkStyles[0].iconColor }} />
                </div>
                <CardTitle className="text-primary-heading font-semibold">
                  {t("help.quickLinkCategorization")}
                </CardTitle>
                <CardDescription className="card-description-text" style={primaryTextStyle}>
                  {t("help.quickLinkCategorizationDesc")}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="stat-card" id="faq-general">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <HelpCircle className="w-5 h-5" style={iconStyle} />
                  {t("help.generalQuestions")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q1")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a1")}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-2">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q2")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a2")}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-3">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q3")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a3")}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q4")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a4")}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>

            <Card className="stat-card" id="faq-search">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Search className="w-5 h-5" style={iconStyle} />
                  {t("help.searchNavigation")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="search-1">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q5")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      <p className="mb-3">{t("help.a5Intro")}</p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>{t("help.a5Bullet1")}</li>
                        <li>{t("help.a5Bullet2")}</li>
                        <li>{t("help.a5Bullet3")}</li>
                      </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="search-2">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q6")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a6")}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="search-3">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q7")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>
                          <strong>{t("help.a7Sort1")}</strong>{" "}
                          {t("help.a7Sort1Desc")}
                        </li>
                        <li>
                          <strong>{t("help.a7Sort2")}</strong>{" "}
                          {t("help.a7Sort2Desc")}
                        </li>
                        <li>
                          <strong>{t("help.a7Sort3")}</strong>{" "}
                          {t("help.a7Sort3Desc")}
                        </li>
                        <li>
                          <strong>{t("help.a7Sort4")}</strong>{" "}
                          {t("help.a7Sort4Desc")}
                        </li>
                      </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>

            <Card className="stat-card" id="faq-account">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <UserPlus className="w-5 h-5" style={iconStyle} />
                  {t("help.registrationAndAccount")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="account-1">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q8")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      <ol className="list-decimal list-inside space-y-2 ml-4">
                        <li>{t("help.a8Step1")}</li>
                        <li>{t("help.a8Step2")}</li>
                        <li>{t("help.a8Step3")}</li>
                        <li>{t("help.a8Step4")}</li>
                      </ol>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="account-2">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q9")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a9Intro")}
                      <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                        <li>{t("help.a9Req1")}</li>
                        <li>{t("help.a9Req2")}</li>
                        <li>{t("help.a9Req3")}</li>
                        <li>{t("help.a9Req4")}</li>
                        <li>{t("help.a9Req5")}</li>
                      </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="account-3">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q10")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a10")}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="account-4">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q11")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a11")}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>

            <Card className="stat-card" id="faq-adding">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Book className="w-5 h-5" style={iconStyle} />
                  {t("help.addingWebsites")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="add-1">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q12")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a12Intro")}
                      <ol className="list-decimal list-inside space-y-2 ml-4 mt-2">
                        <li>{t("help.a12Step1")}</li>
                        <li>{t("help.a12Step2")}</li>
                        <li>{t("help.a12Step3")}</li>
                        <li>{t("help.a12Step4")}</li>
                        <li>{t("help.a12Step5")}</li>
                      </ol>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="add-2">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q13")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a13")}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="add-3">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q14")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a14")}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="add-4">
                    <AccordionTrigger style={primaryTextStyle}>
                      {t("help.q15")}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-body" style={answerTextStyle}>
                      {t("help.a15")}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>

            <Card className="stat-card" style={infoCardStyle}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
                  <Mail className="w-5 h-5" style={iconStyle} />
                  {t("help.needMoreHelp")}
                </CardTitle>
                <CardDescription className="card-description-text" style={primaryTextStyle}>
                  {t("help.needMoreHelpDesc")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-body" style={primaryTextStyle}>{t("help.supportTeam")}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    className="btn-primary gap-2"
                    onClick={() => window.location.href = `mailto:${contactEmail}`}
                  >
                    <Mail className="w-4 h-4" />
                    {contactEmail}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("impressum")}
                  >
                    {t("help.viewContactInfo")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer navigate={navigate} />
    </div>
  );
}
